import { getAllowedLanguages } from "@stss/core";
import type { GetMeetingsParams, Meeting } from "@stss/core/src/video-api";
import type { Pagination, SupportedLanguagesResponse } from "@stss/types";
import { Table, Modal, MeetingForm, NoSearchResultsFound } from "@stss/ui";
import classNames from "classnames";
import React, { useEffect, useState, useRef } from "react";

import dayjs from "@calcom/dayjs";
import Shell from "@calcom/features/shell/Shell";
import type { TRPCClientErrorLike } from "@calcom/trpc/client";
import { trpc } from "@calcom/trpc/react";
import {
  Button,
  ButtonGroup,
  showToast,
  Dropdown,
  DropdownItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  CheckboxField,
} from "@calcom/ui";
import {
  ExternalLink,
  Clipboard,
  MoreHorizontal,
  Send,
  Trash,
  Edit,
  Video,
} from "@calcom/ui/components/icon";

const ALLOW_SMS = true;

const transformPagination = (apiPagination: {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}) => {
  return {
    totalRecords: apiPagination.total,
    totalPages: apiPagination.totalPages,
    perPage: apiPagination.limit,
    currentPage: apiPagination.page,
    prevPage: apiPagination.page > 1,
    nextPage: apiPagination.page < apiPagination.totalPages,
  };
};

const formatToUTC = (dateString: string, timeZone: string) => {
  return dayjs.tz(dateString, timeZone).utc().format("YYYY-MM-DD HH:mm:ss");
};

const formatFromUTC = (date: string, timeZone: string, timeFormat = 12) => {
  return dayjs(date)
    .tz(timeZone)
    .format(timeFormat === 12 ? "MMM DD - hh:mm A" : "MMM DD - HH:mm");
};

export type MeetingPageProps = {
  provider: {
    id: number;
    name: string;
    username: string;
    email: string;
    profileId: number;
    timeZone: string;
    timeFormat: number;
  };
};

const FETCH_LIMIT = 10;

type ModifiedMeeting = Meeting & { isExpired: boolean };

export const MeetingsPage = ({ provider }: MeetingPageProps) => {
  const modalRef = useRef<any>(null);
  const [noMeetings, setNoMeetings] = useState(false);
  const [meetings, setMeetings] = useState<ModifiedMeeting[]>([]);
  const [meetingToUpdate, setMeetingToUpdate] = useState<Meeting | undefined>(undefined);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [closedCaptionLanguages, setClosedCaptionLanguages] = useState<SupportedLanguagesResponse["lpt"]>([]);
  const [params, setParams] = useState<GetMeetingsParams>({
    providerId: provider.id,
    limit: FETCH_LIMIT,
    page: 1,
    type: "ONE_TIME",
    status: "PENDING",
    excludeExpired: true,
  });

  useEffect(() => {
    getAllowedLanguages()
      .then(({ lpt }) => setClosedCaptionLanguages(lpt))
      .catch((e) => {
        showToast("Error getting allowed languages", "error");
        console.error("Error getting allowed languages", e);
      });
  }, []);

  const meetingsQuery = trpc.viewer.proxy.getMeetings.useQuery(params, {
    keepPreviousData: false,
  });

  const createMutation = trpc.viewer.proxy.createMeeting.useMutation({
    onSuccess: () => {
      updateRequestParams({ status: "PENDING", page: 1 });
      showToast("Meeting created successfully", "success");
      modalRef.current?.close();
    },
    onError: (error: TRPCClientErrorLike<any>) => {
      console.log("Failed to create the meeting:", error);
      showToast(error.message, "error");
    },
  });

  const updateMutation = trpc.viewer.proxy.updateMeeting.useMutation({
    onSuccess: () => {
      meetingsQuery.refetch(); // Refetch the meetings list after creating a new meeting
      showToast("Meeting updated successfully", "success");
      modalRef.current?.close();
    },
    onError: (error: TRPCClientErrorLike<any>) => {
      console.error("Failed to update the meeting:", error);
      showToast(error.message, "error");
    },
  });

  const deleteMutation = trpc.viewer.proxy.deleteMeeting.useMutation({
    onSuccess: () => {
      meetingsQuery.refetch(); // Refetch the meetings list after creating a new meeting
      showToast("Meeting deleted successfully", "success");
      modalRef.current?.close();
    },
    onError: (error) => {
      console.error("Failed to delete meeting:", error);
      showToast("Failed to delete meeting", "error");
    },
  });

  const sender = trpc.viewer.proxy.sendSMS.useMutation({
    onSuccess: () => showToast("SMS is sent successfully", "success"),
    onError: () => showToast("Failed send SMS", "error"),
  });

  useEffect(() => {
    if (meetingsQuery.data && meetingsQuery.data.data.length > 0) {
      setPagination(transformPagination(meetingsQuery.data.pagination));
      setNoMeetings(false);
      setMeetings(
        meetingsQuery.data.data.map((meeting) => {
          const isExpired = dayjs(meeting.expires).isBefore(dayjs());
          return { ...meeting, isExpired: isExpired };
        })
      );
    } else {
      setMeetings([]);
      setNoMeetings(true);
    }
  }, [meetingsQuery.data]);

  useEffect(() => {
    meetingsQuery.refetch();
  }, [params]);

  useEffect(() => {
    if (meetingToUpdate) {
      modalRef.current?.open();
    }
  }, [meetingToUpdate]);

  const isLoading = meetingsQuery.data === undefined || createMutation.isLoading || updateMutation.isLoading;

  const updateRequestParams = (params: Partial<GetMeetingsParams>) => {
    setParams((prev) => ({ ...prev, ...params }));
  };

  const sendSMSLink = async (meeting: Meeting) => {
    if (!meeting.participant.phone) {
      alert("Sorry, but participant phone number is missing");
      return;
    }
    const body = `Hi, ${meeting.participant.name}! You have a meeting scheduled with ${provider.name}. Click on the link to join the meeting: ${meeting.links.direct.patient}`;
    sender.mutate({ phoneNumber: meeting.participant.phone, body: body, sender: "ST Hotline" });
  };

  return (
    <Shell
      title="Meetings"
      heading={
        <div className="mb-4">
          <span>Meetings</span>
        </div>
      }
      subtitle="You can manage your scheduled meetings here">
      <section className="mt-4">
        <div className="md:flex md:items-center md:justify-between">
          <ButtonGroup>
            <Button
              color={params.status === "PENDING" ? "primary" : "secondary"}
              onClick={() => updateRequestParams({ status: "PENDING", page: 1, excludeExpired: true })}>
              Pending
            </Button>
            <Button
              color={params.status === "CANCELED" ? "primary" : "secondary"}
              onClick={() => updateRequestParams({ status: "CANCELED", page: 1, excludeExpired: false })}>
              Canceled
            </Button>
            <Button
              color={params.status === "COMPLETED" ? "primary" : "secondary"}
              onClick={() => updateRequestParams({ status: "COMPLETED", page: 1, excludeExpired: false })}>
              Completed
            </Button>
          </ButtonGroup>
          <Button className="ml-auto" disabled={isLoading} onClick={() => modalRef.current?.open()}>
            Schedule a new meeting
          </Button>
        </div>
        <div className="mt-4">
          <CheckboxField
            description="Exclude expired meetings"
            disabled={params.status !== "PENDING"}
            onChange={(e) => {
              updateRequestParams({ excludeExpired: e.target.checked, page: 1 });
            }}
            checked={params.excludeExpired}
          />
        </div>
        <div className="mt-6 flex flex-col">
          {!isLoading && noMeetings ? (
            <NoSearchResultsFound type="no-data" singular="meeting" plural="meetings" searchQuery="" />
          ) : (
            <Table
              defaultPerPage={10}
              rowNumber
              isLoading={isLoading}
              paginationProps={
                pagination
                  ? {
                      pagination,
                      setCurrentPage: (page) => updateRequestParams({ page }),
                      setPerPage: (limit) => updateRequestParams({ limit, page: 1 }),
                    }
                  : undefined
              }
              cols={[
                { id: "id", children: "id" },
                { id: "start", children: "Start time" },
                { id: "expires", children: "Expires" },
                { id: "status", children: "Status" },
                { id: "name", children: "Participant name" },
                { id: "email", children: "Email" },
                { id: "phone", children: "Phone" },
                { id: "join", children: "Join" },
                { id: "actions", children: "Actions" },
              ]}
              rows={meetings.map((meeting) => ({
                id: meeting.id,
                cells: [
                  {
                    id: "id",
                    children: meeting.id,
                  },
                  {
                    id: "start",
                    children: formatFromUTC(meeting.startTime, provider.timeZone, provider.timeFormat),
                  },
                  {
                    id: "expires",
                    children: (
                      <span className={classNames(meeting.isExpired && "text-orange-500")}>
                        {formatFromUTC(meeting.expires, provider.timeZone, provider.timeFormat)}
                      </span>
                    ),
                  },
                  {
                    id: "status",
                    children: meeting.status,
                  },
                  {
                    id: "name",
                    children: meeting.participant.name,
                  },
                  {
                    id: "email",
                    children: meeting.participant.email,
                  },
                  {
                    id: "phone",
                    children: meeting.participant.phone,
                  },
                  {
                    id: "join",
                    children: (
                      <div>
                        {!meeting.isExpired && (
                          <Tooltip content="Join meeting application">
                            <Button
                              color="secondary"
                              variant="icon"
                              className="max-w-8 mt-2 bg-blue-500"
                              href={`/meeting?meetingUrl=${encodeURIComponent(
                                meeting.links.embed.provider
                              )}&type=ONE_TIME`}
                              StartIcon={Video}
                            />
                          </Tooltip>
                        )}
                      </div>
                    ),
                  },
                  {
                    id: "actions",
                    children: (
                      <Dropdown modal={false}>
                        <DropdownMenuTrigger asChild className="radix-state-open:rounded-r-md">
                          <Button type="button" variant="icon" color="secondary" StartIcon={MoreHorizontal} />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          {!meeting.isExpired && (
                            <>
                              {meeting.participant.phone && ALLOW_SMS ? (
                                <DropdownMenuItem>
                                  <DropdownItem
                                    type="button"
                                    StartIcon={Send}
                                    onClick={() => sendSMSLink(meeting)}>
                                    Send patient link via SMS
                                  </DropdownItem>
                                </DropdownMenuItem>
                              ) : null}
                              <DropdownMenuItem>
                                <DropdownItem
                                  type="button"
                                  StartIcon={Clipboard}
                                  onClick={() => {
                                    navigator.clipboard.writeText(meeting.links.direct.patient);
                                    showToast("Patient's link is copied to clipboard", "success");
                                  }}>
                                  Copy patient link
                                </DropdownItem>
                              </DropdownMenuItem>
                              <DropdownMenuItem>
                                <DropdownItem
                                  StartIcon={ExternalLink}
                                  target="_blank"
                                  href={meeting.links.direct.provider}>
                                  Join in a new tab
                                </DropdownItem>
                              </DropdownMenuItem>
                              <DropdownMenuItem>
                                <DropdownItem
                                  StartIcon={Edit}
                                  type="button"
                                  onClick={() =>
                                    setMeetingToUpdate({
                                      ...meeting,
                                      startTime: dayjs(meeting.startTime)
                                        .tz(provider.timeZone)
                                        .format("YYYY-MM-DDTHH:mm"),
                                    })
                                  }>
                                  Edit meeting
                                </DropdownItem>
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuItem>
                            <DropdownItem
                              className="text-red-500"
                              disabled={meeting.status === "CANCELED" || meeting.status === "COMPLETED"}
                              StartIcon={Trash}
                              type="button"
                              onClick={() => {
                                if (confirm("Are you sure you want to delete this meeting?")) {
                                  console.log("delete meeting", meeting.id);
                                  deleteMutation.mutate({ meetingId: meeting.id });
                                }
                              }}>
                              Cancel meeting
                            </DropdownItem>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </Dropdown>
                    ),
                  },
                ],
              }))}
            />
          )}
        </div>
      </section>
      <Modal
        ref={modalRef}
        closeOnWrapper={true}
        onClosed={() => {
          setMeetingToUpdate(undefined);
        }}
        className="max-w-xl">
        <h2 className="text-subtle mb-6 text-center text-lg">
          {meetingToUpdate ? "Edit meeting" : "Schedule a new meeting"}
        </h2>
        <MeetingForm
          languageOptions={closedCaptionLanguages.map((lang) => ({
            value: lang.long_abbreviation || lang.abbreviation,
            label: `${lang.language} (${lang.long_abbreviation || lang.abbreviation})`,
          }))}
          disabled={isLoading}
          providerId={provider.id}
          meeting={meetingToUpdate}
          onCancel={() => {
            modalRef.current?.close();
            setMeetingToUpdate(undefined);
          }}
          onSubmit={(data) => {
            if (meetingToUpdate) {
              updateMutation.mutate({
                meetingId: meetingToUpdate.id,
                type: meetingToUpdate.type,
                startTime: formatToUTC(data.startTime, provider.timeZone),
                participant: data.participant,
              });
            } else {
              createMutation.mutate({ ...data, startTime: formatToUTC(data.startTime, provider.timeZone) });
            }
          }}
        />
      </Modal>
    </Shell>
  );
};

export default MeetingsPage;
